import { getErrorMessage } from "@/utils/errorHelpers";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { authorAPI, bonusesAPI, teamAPI, partnerAPI, kycAPI } from "@/services/api";
import { TrendingUp, Users, Star, Zap, CheckCircle, XCircle, ShieldCheck, QrCode, ClipboardList } from "lucide-react";
import { formatCurrency, PARTNER_LEVELS } from "@/utils/constants";
import { toast } from "sonner";
import { PageHeader, PageShell } from "@/components/app/page";

const DEFAULT_STARTUP_CHECKLIST = {
  sim_attended: false,
  sim_date: "",
  manual_read: false,
  sponsor_interview_done: false,
  coaching_done: false,
  basic_training_done: false,
  has_agrement: false,
  has_phone: false,
  has_computing_device: false,
  has_email_or_social: false,
  has_mobile_money_or_bank: false,
  has_manual: false,
  has_training_notebook: false,
  has_agenda: false,
  has_contact_list_30: false,
  coaching_notes: "",
};

const STARTUP_SECTIONS = [
  {
    title: "SIM et coaching",
    items: [
      ["sim_attended", "Participation a une SIM"],
      ["manual_read", "Manuel des procedures lu"],
      ["sponsor_interview_done", "Entretien avec le parrain"],
      ["coaching_done", "Coaching de demarrage effectue"],
      ["basic_training_done", "Formations de base suivies"],
    ],
  },
  {
    title: "Equipement de depart",
    items: [
      ["has_agrement", "Agrement disponible"],
      ["has_phone", "Telephone disponible"],
      ["has_computing_device", "Support informatique disponible"],
      ["has_email_or_social", "Email ou reseau social actif"],
      ["has_mobile_money_or_bank", "Mobile Money ou compte bancaire"],
      ["has_manual", "Manuel d'information MMJ"],
      ["has_training_notebook", "Bloc-notes de formation"],
      ["has_agenda", "Agenda"],
      ["has_contact_list_30", "Liste de 30 personnes a contacter"],
    ],
  },
];

export default function PartnerDashboard({ user }) {
  const [bonusSummary, setBonusSummary] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activationStatus, setActivationStatus] = useState(null);
  const [startupChecklist, setStartupChecklist] = useState(null);
  const [startupProgress, setStartupProgress] = useState({ completed: 0, total: 14, percent: 0 });
  const [startupSaving, setStartupSaving] = useState(false);
  const [authorProducts, setAuthorProducts] = useState([]);
  const [authorStatsForm, setAuthorStatsForm] = useState({ product_id: "", start_date: "", end_date: "" });
  const [authorStats, setAuthorStats] = useState(null);
  const [authorStatsLoading, setAuthorStatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [kycUpdateLoading, setKycUpdateLoading] = useState(false);
  const [kycUpdate, setKycUpdate] = useState({
    nom: "",
    postnom: "",
    prenom: "",
    telephone: "",
    numero_identite: "",
    mobile_money_intitule: "",
    mobile_money_numero: "",
    reason: "",
  });

  const loadDashboardData = useCallback(async () => {
    const [bonusRes, teamRes, activRes, startupRes, authorProductsRes] = await Promise.allSettled([
      bonusesAPI.getSummary({ month: getCurrentMonthKey() }),
      teamAPI.getMembers(),
      partnerAPI.getActivationStatus(),
      partnerAPI.getStartupChecklist(),
      authorAPI.listProducts(),
    ]);

    if (bonusRes.status === "fulfilled") {
      setBonusSummary(bonusRes.value.data);
    } else {
      console.error("Error loading bonus summary", bonusRes.reason);
      setBonusSummary({ total: 0 });
    }

    if (teamRes.status === "fulfilled") {
      setTeamMembers(teamRes.value.data);
    } else {
      console.error("Error loading team members", teamRes.reason);
      setTeamMembers([]);
    }

    if (activRes.status === "fulfilled") {
      setActivationStatus(activRes.value.data);
    } else {
      console.error("Error loading activation status", activRes.reason);
      setActivationStatus({
        is_active_pae: user.is_active_pae || false,
        is_active_par: user.is_active_par || false,
        total_achats: 0,
        total_bonus: 0,
        pae_threshold: 1,
      });
    }

    if (startupRes.status === "fulfilled") {
      setStartupChecklist({ ...DEFAULT_STARTUP_CHECKLIST, ...(startupRes.value.data.checklist || {}) });
      setStartupProgress(startupRes.value.data.progress || { completed: 0, total: 14, percent: 0 });
    } else {
      setStartupChecklist(DEFAULT_STARTUP_CHECKLIST);
    }

    if (authorProductsRes.status === "fulfilled") {
      const products = Array.isArray(authorProductsRes.value.data) ? authorProductsRes.value.data : [];
      setAuthorProducts(products);
      if (products.length > 0) {
        setAuthorStatsForm((prev) => prev.product_id ? prev : { ...prev, product_id: products[0].id });
      }
    }

    setLoading(false);
  }, [user.is_active_pae, user.is_active_par]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleActivatePAR = async () => {
    setActivating(true);
    try {
      const res = await partnerAPI.requestActivation();
      toast.success(res.data.message || "Demande d'activation envoyée !");
      const statusRes = await partnerAPI.getActivationStatus();
      setActivationStatus(statusRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur d'activation"));
    } finally {
      setActivating(false);
    }
  };

  const handleKycUpdateSubmit = async (e) => {
    e.preventDefault();
    setKycUpdateLoading(true);
    try {
      const fd = new FormData();
      Object.entries(kycUpdate).forEach(([key, value]) => fd.append(key, value || ""));
      await kycAPI.requestUpdate(fd);
      toast.success("Demande de modification KYC transmise.");
      setKycUpdate({
        nom: "",
        postnom: "",
        prenom: "",
        telephone: "",
        numero_identite: "",
        mobile_money_intitule: "",
        mobile_money_numero: "",
        reason: "",
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur demande KYC"));
    } finally {
      setKycUpdateLoading(false);
    }
  };

  const saveStartupChecklist = async (nextChecklist) => {
    setStartupChecklist(nextChecklist);
    setStartupSaving(true);
    try {
      const res = await partnerAPI.updateStartupChecklist(nextChecklist);
      setStartupChecklist({ ...DEFAULT_STARTUP_CHECKLIST, ...(res.data.checklist || {}) });
      setStartupProgress(res.data.progress || startupProgress);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur checklist demarrage"));
    } finally {
      setStartupSaving(false);
    }
  };

  const toggleStartupItem = (key, value) => {
    saveStartupChecklist({ ...DEFAULT_STARTUP_CHECKLIST, ...(startupChecklist || {}), [key]: !!value });
  };

  const extractAuthorStats = async (e) => {
    e.preventDefault();
    if (!authorStatsForm.product_id) return;
    setAuthorStatsLoading(true);
    try {
      const res = await authorAPI.getProductSalesStats(authorStatsForm.product_id, {
        start_date: authorStatsForm.start_date || undefined,
        end_date: authorStatsForm.end_date || undefined,
      });
      setAuthorStats(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur extraction statistiques"));
    } finally {
      setAuthorStatsLoading(false);
    }
  };

  const setStartupField = (key, value) => {
    saveStartupChecklist({ ...DEFAULT_STARTUP_CHECKLIST, ...(startupChecklist || {}), [key]: value });
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }
// Calcul du prochain palier
const getNextLevelInfo = (points, currentLevel) => {
  if (points < 12) return { next: "P-12", target: 12, color: "bg-blue-500" };
  if (points < 24) return { next: "P-24", target: 24, color: "bg-purple-500" };
  if (points < 36) return { next: "P-36", target: 36, color: "bg-indigo-500" };
  if (points < 50) return { next: "Étoile", target: 50, color: "bg-yellow-500" };
  return { next: "Maximum atteint", target: 50, color: "bg-yellow-600" };
};

const levelInfo = getNextLevelInfo(user.points_activation, user.partner_level);
const progressPercent = Math.min((user.points_activation / levelInfo.target) * 100, 100);
const paeCurrent = activationStatus?.total_achats ?? activationStatus?.total_bonus ?? 0;
const paeThreshold = activationStatus?.pae_threshold ?? 1;
const paeRemaining = Math.max(paeThreshold - paeCurrent, 0);
const referralLink = `${window.location.origin}/register?parrain=${encodeURIComponent(user.id)}`;
const referralQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=10&data=${encodeURIComponent(referralLink)}`;
  const statCards = [
    {
      title: "Niveau actuel",
      value: PARTNER_LEVELS[user.partner_level]?.label || "N/A",
      icon: Star,
      color: "bg-yellow-100 text-yellow-800",
    },
    {
    title: "Points Activation (PAR)",
    // On montre clairement le cumul des points d'activation
    value: `${user.points_activation.toFixed(2)} MC`,
    icon: Zap,
    color: "bg-orange-100 text-orange-800",
  },
  {
    title: "Membres d'équipe",
    value: teamMembers.length,
    icon: Users,
    color: "bg-blue-100 text-blue-800",
  },
  {
    title: "Bonus du mois",
    // Le compteur partenaire repart de zero chaque mois; les anciens mois restent dans les archives.
    value: formatCurrency(bonusSummary?.total || 0),
    icon: TrendingUp,
    color: "bg-purple-100 text-purple-800",
  },
];

  return (
    <PageShell data-testid="partner-dashboard">
      {/* Bannière de vérification alignée sur la logique Alain */}
{!user.is_verified && (
  <div className="flex flex-col gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="verification-banner">
    <div className="flex items-center gap-3">
      <ShieldCheck size={24} className="text-red-600" />
      <div>
        <p className="font-semibold text-red-800">Compte en attente d'activation</p>
        <p className="text-sm text-red-700">
          Votre identité doit être validée par l'administration pour débloquer
          <strong> vos privilèges de partenaire.</strong> veuillez soumettre votre KYC dès que possible pour profiter pleinement de votre compte.
        </p>
      </div>
    </div>
    <a href="/verification">
      <Button className="w-full shrink-0 bg-red-600 text-white hover:bg-red-700 sm:w-auto">
        Soumettre mon KYC
      </Button>
    </a>
  </div>
)}

      <PageHeader title="Tableau de bord partenaire" description={`Bienvenue, ${user.full_name}`} />
      
      {/* ID de parrainage */}
      {user.is_verified ? (
      <div className="rounded-2xl border border-[#E6E4E0] bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-[#5C625F] mb-1">Votre ID de parrainage</p>
            <p className="text-lg font-bold text-[#2A4B46] font-mono" data-testid="partner-id">
              {user.id}
            </p>
            <p className="text-xs text-[#5C625F] mt-1">
              Partagez cet ID avec vos filleuls pour qu'ils l'utilisent lors de leur inscription
            </p>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(user.id);
              alert("ID copié dans le presse-papiers !");
            }}
            className="rounded-xl bg-[#2A4B46] px-4 py-2 text-white transition-colors hover:bg-[#1f3a36]"
            data-testid="copy-id-button"
          >
            Copier l'ID
          </button>
        </div>
        
        <div className="border-t border-[#E6E4E0] pt-4">
          <p className="text-sm text-[#5C625F] mb-2">Lien de parrainage personnalisé</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              readOnly
              value={referralLink}
              className="min-w-0 flex-1 rounded-xl border border-[#E6E4E0] bg-[#F3EFE9] px-3 py-2 text-sm font-mono"
              data-testid="referral-link"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(referralLink);
                alert("Lien copié dans le presse-papiers !");
              }}
              className="whitespace-nowrap rounded-xl bg-[#D4A373] px-4 py-2 text-white transition-colors hover:bg-[#c49362]"
              data-testid="copy-link-button"
            >
              Copier le lien
            </button>
          </div>
          <p className="text-xs text-[#5C625F] mt-2">
            Partagez ce lien avec vos filleuls pour qu'ils s'inscrivent directement avec votre ID
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 sm:items-center">
            <a href={referralLink} target="_blank" rel="noopener noreferrer" className="block w-44 h-44 rounded-lg border border-[#E6E4E0] bg-white p-3 hover:border-[#D4A373] transition-colors" data-testid="referral-qr-link">
              <img src={referralQrUrl} alt={`QR Code parrainage ${user.id}`} className="w-full h-full object-contain" data-testid="referral-qr-image" />
            </a>
            <div className="text-sm text-[#5C625F]">
              <div className="flex items-center gap-2 font-semibold text-[#2A4B46] mb-1">
                <QrCode size={18} /> QR Code de recrutement
              </div>
              <p>Un scan ouvre directement l'inscription avec votre ID de parrain déjà saisi.</p>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm" data-testid="referral-locked">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-900">Programme de parrainage verrouille</p>
              <p className="mt-1 text-sm leading-6 text-amber-800">
                Verifiez votre KYC pour bénéficier du programme de parrainage. Votre code et votre lien de parrainage seront disponibles après validation de votre identite.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Activation Status */}
      {activationStatus && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className={`border-[#E6E4E0] shadow-sm border-l-4 ${activationStatus.is_active_pae ? "border-l-green-500" : "border-l-yellow-500"}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#5C625F]">Volet Édition (PAE)</CardTitle>
              {activationStatus.is_active_pae ? (
                <CheckCircle size={20} className="text-green-500" />
              ) : (
                <XCircle size={20} className="text-yellow-500" />
              )}
            </CardHeader>
            <CardContent>
              <p className={`text-lg font-bold ${activationStatus.is_active_pae ? "text-green-600" : "text-yellow-600"}`}>
                {activationStatus.is_active_pae ? "Actif" : "Inactif"}
              </p>
              <p className="text-xs text-[#5C625F] mt-1">
                Condition : le PAE s'active automatiquement lorsque vos achats propres de produits edition validés atteignent {formatCurrency(paeThreshold)} sur le mois courant.
                {" "}Actuel : {formatCurrency(paeCurrent)}.
                {!activationStatus.is_active_pae && paeRemaining > 0 ? ` Reste : ${formatCurrency(paeRemaining)}.` : ""}
              </p>
            </CardContent>
          </Card>

          {/* Carte PAR modifiée */}
<Card className={`border-[#E6E4E0] shadow-sm border-l-4 ${activationStatus.is_active_par ? "border-l-green-500" : "border-l-yellow-500"}`}>
  <CardHeader className="flex flex-row items-center justify-between pb-2">
    <CardTitle className="text-sm font-medium text-[#5C625F]">Volet Relationnel (PAR)</CardTitle>
    {activationStatus.is_active_par ? (
      <CheckCircle size={20} className="text-green-500" />
    ) : (
      <XCircle size={20} className="text-yellow-500" />
    )}
  </CardHeader>
            <CardContent>
              {activationStatus.is_active_par ? (
                <p className="text-lg font-bold text-green-600">Actif</p>
              ) : (
                <>
                  <p className="text-lg font-bold text-yellow-600 mb-3">Inactif</p>
                  <Button
                    className="w-full bg-[#D4A373] hover:bg-[#c49362] text-white"
                    onClick={handleActivatePAR}
                    disabled={activating}
                    data-testid="activate-par-dashboard-button"
                  >
                    <Zap size={16} className="mr-2" />
                    {activating ? "Envoi..." : "Demander l'activation (1 MC)"}
                  </Button>
                  <p className="text-xs text-[#5C625F] mt-2">
                    L'admin doit valider votre paiement pour activer votre compte
                  </p>
                </>
              )}
              <p className="text-xs text-[#5C625F] mt-2">
                Condition : activation manuelle mensuelle de 1 MC, validee par l'administration, pour recevoir les bonus relationnels.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {startupChecklist && (
        <Card className="border-[#E6E4E0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              <ClipboardList size={20} className="text-[#D4A373]" /> SIM, coaching et demarrage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-[#2A4B46]">{startupProgress.completed}/{startupProgress.total} elements completes</span>
                <span className="text-[#5C625F]">{startupProgress.percent}%</span>
              </div>
              <Progress value={startupProgress.percent} className="h-2" />
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              {STARTUP_SECTIONS.map((section) => (
                <div key={section.title} className="space-y-3 rounded-xl border border-[#E6E4E0] p-4">
                  <p className="font-semibold text-[#2A4B46]">{section.title}</p>
                  <div className="space-y-2">
                    {section.items.map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-start gap-2 text-sm text-[#5C625F]">
                        <Checkbox checked={!!startupChecklist[key]} onCheckedChange={(value) => toggleStartupItem(key, value)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
              <div>
                <Label>Date SIM</Label>
                <Input
                  type="date"
                  value={startupChecklist.sim_date || ""}
                  onChange={(e) => setStartupChecklist({ ...startupChecklist, sim_date: e.target.value })}
                  onBlur={(e) => setStartupField("sim_date", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes de coaching</Label>
                <Textarea
                  value={startupChecklist.coaching_notes || ""}
                  onChange={(e) => setStartupChecklist({ ...startupChecklist, coaching_notes: e.target.value })}
                  onBlur={(e) => setStartupField("coaching_notes", e.target.value)}
                  placeholder="Resume du coaching, prochaines actions, besoins du filleul..."
                  className="mt-1"
                />
              </div>
            </div>

            {startupSaving && <p className="text-xs text-[#8A8F8C]">Sauvegarde en cours...</p>}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-[#E6E4E0] shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-[#5C625F]">{stat.title}</CardTitle>
                <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center`}>
                  <Icon size={16} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#2A4B46]" data-testid={`stat-${index}`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
            
          );
        })}
      </div>

      {authorProducts.length > 0 && (
        <Card className="mb-8 border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              Statistiques de vente auteur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={extractAuthorStats} className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px_160px_auto] md:items-end">
              <div>
                <Label>Produit</Label>
                <select
                  value={authorStatsForm.product_id}
                  onChange={(e) => setAuthorStatsForm({ ...authorStatsForm, product_id: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-[#E6E4E0] bg-white px-3 text-sm"
                >
                  {authorProducts.map((product) => (
                    <option key={product.id} value={product.id}>{product.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Debut</Label>
                <Input type="date" value={authorStatsForm.start_date} onChange={(e) => setAuthorStatsForm({ ...authorStatsForm, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="date" value={authorStatsForm.end_date} onChange={(e) => setAuthorStatsForm({ ...authorStatsForm, end_date: e.target.value })} className="mt-1" />
              </div>
              <Button type="submit" disabled={authorStatsLoading} className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
                {authorStatsLoading ? "Extraction..." : "Extraire"}
              </Button>
            </form>

            {authorStats && (
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl bg-[#F3EFE9] p-4">
                  <p className="text-sm text-[#5C625F]">Ventes</p>
                  <p className="text-2xl font-bold text-[#2A4B46]">{authorStats.total_sales}</p>
                </div>
                <div className="rounded-xl bg-[#F3EFE9] p-4">
                  <p className="text-sm text-[#5C625F]">Montant</p>
                  <p className="text-2xl font-bold text-[#2A4B46]">{formatCurrency(authorStats.total_amount_mc || 0)}</p>
                </div>
                <div className="rounded-xl bg-[#F3EFE9] p-4">
                  <p className="text-sm text-[#5C625F]">Acheteurs uniques</p>
                  <p className="text-2xl font-bold text-[#2A4B46]">{authorStats.unique_buyers}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a
                href="/team"
                className="block p-4 bg-[#F3EFE9] rounded-lg hover:bg-[#E6E4E0] transition-colors"
                data-testid="quick-action-view-team"
              >
                <p className="font-semibold text-[#2A4B46]">Voir mon équipe</p>
                <p className="text-sm text-[#5C625F]">{teamMembers.length} membres</p>
              </a>
              <a
                href="/bonuses"
                className="block p-4 bg-[#F3EFE9] rounded-lg hover:bg-[#E6E4E0] transition-colors"
                data-testid="quick-action-view-bonuses"
              >
                <p className="font-semibold text-[#2A4B46]">Mes bonus du mois</p>
                <p className="text-sm text-[#5C625F]">{formatCurrency(bonusSummary?.total || 0)}</p>
              </a>
              <a
                href="/catalogue"
                className="block p-4 bg-[#F3EFE9] rounded-lg hover:bg-[#E6E4E0] transition-colors"
                data-testid="quick-action-view-catalogue"
              >
                <p className="font-semibold text-[#2A4B46]">Parcourir le catalogue</p>
                <p className="text-sm text-[#5C625F]">Découvrir les produits</p>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              Progression du niveau
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#5C625F] mb-1">Niveau actuel</p>
                <div className="text-2xl font-bold text-[#2A4B46]">
                  {PARTNER_LEVELS[user.partner_level]?.label || "P-00"}
                </div>
              </div>
              <div>
                <p className="text-sm text-[#5C625F] mb-1">Points d'Ascension</p>
                <div className="text-lg font-semibold text-[#2A4B46]">{user.points_ascension || 0} MC</div>
              </div>
              <div className="text-xs text-[#5C625F]">
                <p>Niveaux : P-00 (0) → P-12 (12) → P-24 (24) → P-36 (36) → E-50 (50)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {user.is_verified && (
        <Card className="border-[#E6E4E0] shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              Demande de modification KYC
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleKycUpdateSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <div><Label>Nom</Label><Input value={kycUpdate.nom} onChange={(e) => setKycUpdate({ ...kycUpdate, nom: e.target.value })} /></div>
                <div><Label>Post-nom</Label><Input value={kycUpdate.postnom} onChange={(e) => setKycUpdate({ ...kycUpdate, postnom: e.target.value })} /></div>
                <div><Label>Prenom</Label><Input value={kycUpdate.prenom} onChange={(e) => setKycUpdate({ ...kycUpdate, prenom: e.target.value })} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Telephone</Label><Input value={kycUpdate.telephone} onChange={(e) => setKycUpdate({ ...kycUpdate, telephone: e.target.value })} /></div>
                <div><Label>N identite</Label><Input value={kycUpdate.numero_identite} onChange={(e) => setKycUpdate({ ...kycUpdate, numero_identite: e.target.value })} /></div>
                <div><Label>Mobile Money - intitule</Label><Input value={kycUpdate.mobile_money_intitule} onChange={(e) => setKycUpdate({ ...kycUpdate, mobile_money_intitule: e.target.value })} /></div>
                <div><Label>Mobile Money - numéro</Label><Input value={kycUpdate.mobile_money_numero} onChange={(e) => setKycUpdate({ ...kycUpdate, mobile_money_numero: e.target.value })} /></div>
              </div>
              <div>
                <Label>Motif de la modification</Label>
                <Textarea value={kycUpdate.reason} onChange={(e) => setKycUpdate({ ...kycUpdate, reason: e.target.value })} placeholder="Expliquez pourquoi ces informations doivent etre modifiees." />
              </div>
              <p className="text-xs text-[#5C625F]">
                La modification suivra une double validation : Rang A/FULL propose, puis un FULL different approuve.
              </p>
              <Button type="submit" disabled={kycUpdateLoading} className="bg-[#2A4B46] text-white">
                {kycUpdateLoading ? "Envoi..." : "Soumettre la demande"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}

function getCurrentMonthKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
